import { useState, useEffect, useRef } from "react";

const LS_KEY_TIMER_STATE = "wall:timer_state";

const TimerMini = () => {
  const [visible, setVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [reverse, setReverse] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [duration, setDuration] = useState(0);
  const [running, setRunning] = useState(false);

  const intervalRef = useRef(null);

  // --- Utility: Compute Remaining Time ---
  const getRemainingTime = (startedAt, duration) => {
    if (!startedAt || !duration) return duration;
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    return Math.max(duration - elapsed, 0);
  };

  // --- Load state from localStorage ---
  const syncFromStorage = () => {
    try {
      const raw = localStorage.getItem(LS_KEY_TIMER_STATE);
      if (!raw) return;
      const saved = JSON.parse(raw);
      const { duration, reverse, startedAt, paused } = saved || {};

      setDuration(duration || 0);
      setReverse(!!reverse);
      setStartedAt(startedAt || null);

      if (paused) {
        // Prepared but not started
        setRunning(false);
        setSecondsLeft(duration || 0);
      } else if (startedAt && duration) {
        // Active countdown
        setRunning(true);
        setSecondsLeft(getRemainingTime(startedAt, duration));
      } else {
        setRunning(false);
        setSecondsLeft(0);
      }
    } catch {}
  };

  // --- Setup Broadcast Channel ---
  useEffect(() => {
    const bc = new BroadcastChannel("operator-events");

    bc.onmessage = (event) => {
      const { actionId, payload } = event.data || {};

      if (actionId === "SHOW_TIMER") {
        if (payload.mode === "reset") {
          clearInterval(intervalRef.current);
          setRunning(false);
          setSecondsLeft(0);
          setStartedAt(null);
          localStorage.removeItem(LS_KEY_TIMER_STATE);
          return;
        }

        if (payload.mode === "prepare") {
          clearInterval(intervalRef.current);
          setRunning(false);
          setDuration(payload.duration);
          setSecondsLeft(payload.duration);
          setReverse(!!payload.reverse);
          setStartedAt(null);
          localStorage.setItem(LS_KEY_TIMER_STATE, JSON.stringify(payload));
          return;
        }

        if (payload.mode === "start") {
          clearInterval(intervalRef.current);
          setDuration(payload.duration);
          setReverse(!!payload.reverse);
          setStartedAt(payload.startedAt || Date.now());
          setRunning(true);
          localStorage.setItem(LS_KEY_TIMER_STATE, JSON.stringify(payload));
          return;
        }
      }

      if (actionId === "TOGGLE_MINI_TIMER") {
        const newVisible = !!payload.visible;
        setVisible(newVisible);
        if (newVisible) syncFromStorage();
      }
    };

    // Initial sync on mount
    syncFromStorage();

    return () => bc.close();
  }, []);

  // --- Live Sync with Main Timer ---
  useEffect(() => {
    if (!running || !startedAt) return;

    const update = () => {
      const remaining = getRemainingTime(startedAt, duration);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        setRunning(false);
      }
    };

    update();
    intervalRef.current = setInterval(update, 500);

    return () => clearInterval(intervalRef.current);
  }, [running, startedAt, duration]);

  // --- Format Time ---
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours > 0 ? hours + ":" : ""}${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!visible) return null;

  const progress = reverse
    ? (1 - secondsLeft / duration) * 100
    : (secondsLeft / duration) * 100;

  return (
    <div
      className="flex items-center gap-3 fixed top-6 right-6  px-4 py-2 rounded-xl  z-[9999]"
      style={{ fontFamily: "Etemad" }}
    >
      <img src="/timer-mini/timer-mini.svg" alt="timer" />
      <div className="flex flex-col items-center">
        <span className="text-[44px] font-bold text-white leading-none w-[150px] text-center">
          {formatTime(secondsLeft)}
        </span>
      </div>
      <img
        src="/timer-mini/timer-mini.svg"
        className="rotate-180"
        alt="timer"
      />
    </div>
  );
};

export default TimerMini;
